import * as React from "react";
import { forwardRef } from "react";
import { styled } from "@mui/material/styles";
import MuiTextField, {
  TextFieldProps as MuiTextFieldProps,
} from "@mui/material/TextField";
import {
  COLOR_CONSTANTS,
  SPACING_CONSTANTS,
  FONT_SIZES,
} from "../../constants/styles";

// TextField 사이즈 타입
export type TextFieldSize = "medium" | "small";

// TextField 상태 타입
export type TextFieldState = "enabled" | "disabled" | "error";

/**
 * 단일 행 텍스트 입력 컴포넌트의 props. MUI `TextField` 의 size 만 자체 토큰으로 교체한 래퍼.
 *
 * @example
 * ```tsx
 * <TextField label="이름" placeholder="이름을 입력하세요" />
 * <TextField label="이메일" state="error" helperText="형식이 올바르지 않습니다" showHelperText />
 * <TextField label="검색" startIcon={<SearchIcon />} size="small" />
 * ```
 */
export interface TextFieldProps extends Omit<MuiTextFieldProps, "size"> {
  /** 크기 토큰 (기본 `"medium"`) */
  size?: TextFieldSize;
  /** 상태 토큰 — `disabled`/`error` prop 과 동등 효과 (기본 `"enabled"`) */
  state?: TextFieldState;
  label?: string;
  placeholder?: string;
  helperText?: string;
  /** 라벨 노출 여부 (기본 `true`) */
  showLabel?: boolean;
  /** helperText 노출 여부 (기본 `false` — `error` 표시할 때 명시적으로 켠다) */
  showHelperText?: boolean;
  /** 입력창 왼쪽 끝에 끼울 아이콘 */
  startIcon?: React.ReactNode;
  /** 입력창 오른쪽 끝에 끼울 아이콘 */
  endIcon?: React.ReactNode;
  disabled?: boolean;
  error?: boolean;
  /**
   * 테스트 앵커. 래퍼가 아니라 **실제 input 노드**로 라우팅된다(`slotProps.htmlInput`).
   * `getByTestId().fill()` 이 input 에 직접 입력되어 조용한 실패를 막는다(design Decision 8).
   */
  "data-testid"?: string;
}

/**
 * 사이즈별 입력창 높이(px). getTextFieldSizeStyles 가 이 값으로 입력창 높이를 그린다.
 * TextField 와 같은 행에 놓여 높이를 맞춰야 하는 다른 폼 컨트롤은 리터럴을 복제하지 말고
 * 이 값에서 파생한다.
 */
export const TEXT_FIELD_HEIGHT_PX = {
  small: 32,
  medium: 40,
} as const satisfies Record<TextFieldSize, number>;

// 사이즈별 입력창 안쪽 여백(px). y 는 위·아래, x 는 좌우. 라벨 유무와 무관한 사이즈 값이라
// 라벨을 숨긴 입력창(showLabel={false})도 위·좌우는 이 값을 쓰고 아래만 NO_LABEL_PADDING_BOTTOM_PX 로 바꾼다.
const TEXT_FIELD_PADDING_PX = {
  small: { y: 6, x: 12 },
  medium: { y: 8, x: 12 },
} as const satisfies Record<TextFieldSize, { y: number; x: number }>;

// 라벨을 숨긴 입력창의 아래 여백(px).
const NO_LABEL_PADDING_BOTTOM_PX = 2;

// 사이즈별 스타일 함수
const getTextFieldSizeStyles = (size: TextFieldSize) => {
  switch (size) {
    case "medium":
      return {
        height: `${TEXT_FIELD_HEIGHT_PX.medium}px`,
        padding: `${TEXT_FIELD_PADDING_PX.medium.y}px ${TEXT_FIELD_PADDING_PX.medium.x}px`,
      };
    case "small":
      return {
        height: `${TEXT_FIELD_HEIGHT_PX.small}px`,
        padding: `${TEXT_FIELD_PADDING_PX.small.y}px ${TEXT_FIELD_PADDING_PX.small.x}px`,
      };
    default:
      return {
        height: `${TEXT_FIELD_HEIGHT_PX.medium}px`,
        padding: `${TEXT_FIELD_PADDING_PX.medium.y}px ${TEXT_FIELD_PADDING_PX.medium.x}px`,
      };
  }
};

// 상태별 스타일 함수
const getTextFieldStateStyles = (state: TextFieldState) => {
  switch (state) {
    case "enabled":
      return {
        borderColor: COLOR_CONSTANTS.COLOR.GREY[300],
        labelColor: COLOR_CONSTANTS.text.secondary,
        textColor: COLOR_CONSTANTS.text.primary,
        placeholderColor: COLOR_CONSTANTS.text.disabled,
        backgroundColor: "transparent",
      };
    case "disabled":
      return {
        borderColor: COLOR_CONSTANTS.COLOR.GREY[300],
        labelColor: COLOR_CONSTANTS.text.disabled,
        textColor: COLOR_CONSTANTS.text.disabled,
        placeholderColor: COLOR_CONSTANTS.text.disabled,
        backgroundColor: COLOR_CONSTANTS.COLOR.GREY[50],
      };
    case "error":
      return {
        borderColor: COLOR_CONSTANTS.error.main,
        labelColor: COLOR_CONSTANTS.error.main,
        textColor: COLOR_CONSTANTS.text.primary,
        placeholderColor: COLOR_CONSTANTS.text.disabled,
        backgroundColor: "transparent",
      };
    default:
      return {
        borderColor: COLOR_CONSTANTS.COLOR.GREY[300],
        labelColor: COLOR_CONSTANTS.text.secondary,
        textColor: COLOR_CONSTANTS.text.primary,
        placeholderColor: COLOR_CONSTANTS.text.disabled,
        backgroundColor: "transparent",
      };
  }
};

// 라벨 라인높이. MUI InputLabel 은 ellipsis 용 overflow: hidden 이라, 폰트 크기와 같은 라인높이면
// 한글 어센더가 라인박스 밖으로 나가 잘린다 — 폰트 크기보다 여유 있게 둔다.
const LABEL_LINE_HEIGHT_PX = 16;

// 라벨 고정 위치. 세로 이동은 라인높이의 절반이라 라인높이를 바꿔도 라벨 중심이 제자리에 머문다.
// 이 래퍼는 라벨을 기본 shrink 로 띄우므로(slotProps.inputLabel) 기본·포커스·shrink 세 규칙이
// 이 한 값을 써야 포커스 전환 때 라벨이 움직이지 않는다.
const LABEL_SHRINK_TRANSFORM = `translate(4px, -${LABEL_LINE_HEIGHT_PX / 2}px) scale(1)`;

// 스타일드 TextField 컴포넌트
const StyledTextField = styled(MuiTextField, {
  shouldForwardProp: (prop) => prop !== "state" && prop !== "showLabel",
})<TextFieldProps & { state: TextFieldState; showLabel: boolean }>(
  ({ theme, size = "medium", state, showLabel }) => {
    const sizeStyles = getTextFieldSizeStyles(size);
    const stateStyles = getTextFieldStateStyles(state);
    // 라벨을 숨긴 입력창의 위·좌우 여백. "medium" 이 아닌 size 는 small 값을 쓴다
    // (getTextFieldSizeStyles 의 default 는 medium — 둘은 TextFieldSize 밖의 값에서만 갈린다).
    const noLabelPadding =
      TEXT_FIELD_PADDING_PX[size === "medium" ? "medium" : "small"];

    return {
      // 컨테이너 스타일
      "& .MuiFormControl-root": {
        width: "100%",
        // 라벨이 없을 때 margin 조정하여 위로 올라가는 문제 해결
        ...(showLabel === false && {
          marginTop: 0,
          marginBottom: 0,
          "& .MuiInputLabel-root": {
            display: "none", // 라벨이 없을 때 라벨 요소 자체를 숨김
          },
        }),
      },

      // Input 스타일
      "& .MuiOutlinedInput-root": {
        height: sizeStyles.height,
        borderRadius: SPACING_CONSTANTS.borderRadius.sm,
        backgroundColor: stateStyles.backgroundColor,
        transition: "all 0.2s ease-in-out",
        // 라벨이 없을 때 margin 조정
        ...(showLabel === false && {
          marginTop: 0,
        }),

        "& fieldset": {
          borderColor: stateStyles.borderColor,
          borderWidth: "1px",
        },

        // 호버 상태 - enabled 상태에서만 호버 효과 적용
        "&:hover fieldset": {
          borderColor:
            state === "enabled"
              ? COLOR_CONSTANTS.text.primary
              : stateStyles.borderColor,
          borderWidth: "2px",
        },

        // 포커스 상태 - enabled 상태에서만 포커스 효과 적용
        "&.Mui-focused fieldset": {
          borderColor:
            state === "enabled"
              ? COLOR_CONSTANTS.primary.main
              : stateStyles.borderColor,
          borderWidth: "2px",
        },

        // 비활성화 상태
        "&.Mui-disabled fieldset": {
          borderColor: stateStyles.borderColor,
          borderWidth: "1px",
        },

        // Input 텍스트 스타일
        "& .MuiOutlinedInput-input": {
          padding:
            showLabel === false
              ? `${noLabelPadding.y}px ${noLabelPadding.x}px ${NO_LABEL_PADDING_BOTTOM_PX}px ${noLabelPadding.x}px`
              : sizeStyles.padding,
          fontSize: FONT_SIZES.md,
          fontFamily: "Pretendard, sans-serif",
          fontWeight: 400,
          lineHeight: "24px",
          letterSpacing: "0.15px",
          color: stateStyles.textColor,

          "&::placeholder": {
            color: stateStyles.placeholderColor,
            opacity: 1,
          },
        },

        // Adornment 스타일
        "& .MuiInputAdornment-root": {
          "& .MuiSvgIcon-root": {
            fontSize: FONT_SIZES.xxl,
            color: stateStyles.textColor,
          },
        },
      },

      // 라벨 스타일
      "& .MuiInputLabel-root": {
        fontSize: FONT_SIZES.sm,
        lineHeight: `${LABEL_LINE_HEIGHT_PX}px`,
        letterSpacing: "0.15px",
        fontFamily: "Pretendard, sans-serif",
        fontWeight: 400,
        color: stateStyles.labelColor,
        transform: LABEL_SHRINK_TRANSFORM, // 항상 위쪽 고정, 왼쪽으로 이동
        transition: "all 0.2s ease-in-out",
        backgroundColor: "white", // 배경색으로 outline과 겹침 방지
        padding: `0 ${SPACING_CONSTANTS.spacing[1]}px`, // 좌우 패딩으로 자연스러운 배경

        // 호버 상태에서 라벨 색상 변경 (enabled 상태에서만)
        "&:hover": {
          color:
            state === "enabled"
              ? COLOR_CONSTANTS.text.primary
              : stateStyles.labelColor,
        },

        // 포커스 상태에서 라벨 색상 변경 (enabled 상태에서만)
        "&.Mui-focused": {
          color:
            state === "enabled"
              ? COLOR_CONSTANTS.primary.main
              : stateStyles.labelColor,

          transform: LABEL_SHRINK_TRANSFORM,
        },

        "&.MuiInputLabel-shrink": {
          transform: LABEL_SHRINK_TRANSFORM,
        },
      },

      // Helper Text 스타일
      "& .MuiFormHelperText-root": {
        fontSize: FONT_SIZES.sm,
        fontFamily: "Roboto, sans-serif",
        fontWeight: 400,
        lineHeight: "20px",
        letterSpacing: "0.4px",
        color:
          state === "error"
            ? COLOR_CONSTANTS.error.main
            : COLOR_CONSTANTS.text.secondary,
        marginTop: `${SPACING_CONSTANTS.spacing[1]}px`,
      },
    };
  }
);

export const TextField = forwardRef<HTMLDivElement, TextFieldProps>(
  (
    {
      size = "medium",
      state = "enabled",
      label,
      placeholder,
      helperText,
      showLabel = true,
      showHelperText = false,
      startIcon,
      endIcon,
      disabled,
      error,
      "data-testid": dataTestId,
      ...props
    },
    ref
  ) => {
    // 상태 결정 로직
    const isDisabled = disabled || state === "disabled";
    const hasError = error || state === "error";

    // InputProps 설정
    const inputProps = {
      startAdornment: startIcon,
      endAdornment: endIcon,
    };

    return (
      <StyledTextField
        ref={ref}
        size={size}
        state={state}
        showLabel={showLabel}
        variant="outlined"
        label={showLabel ? label : undefined}
        placeholder={placeholder}
        helperText={showHelperText ? helperText : undefined}
        disabled={isDisabled}
        error={hasError}
        slotProps={{
          input: {
            ...inputProps,
          },
          // testid 를 input 노드까지 라우팅 — getByTestId().fill() 이 직관적으로 동작.
          ...(dataTestId ? { htmlInput: { "data-testid": dataTestId } } : {}),
          inputLabel: {
            shrink: true, // 라벨이 항상 위쪽에 고정되도록 설정
            ...props.InputLabelProps,
          },
        }}
        {...props}
      />
    );
  }
);

TextField.displayName = "TextField";
